import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {css} from '@emotion/core'
import {createMarkup} from '../graphql/datoCmsDataFactory'
import {getTheme, getThemeStyles} from '../util/theme'
import {getLanguageCode} from '../util/locale'

import Layout from '../components/Layout'

import SearchMenuSvgEn from '../images/startpage-firefox-search-menu-en.svg'
import SearchMenuSvgDe from '../images/startpage-firefox-search-menu-de.svg'
import SearchMenuSvgFr from '../images/startpage-firefox-search-menu-fr.svg'
import SearchMenuSvgNl from '../images/startpage-firefox-search-menu-nl.svg'

import firefoxData from '../translations/firefox.json'

// localized search-menu graphics; English is the fallback for all other languages
const searchMenuSvgsByLang = {
    de: SearchMenuSvgDe,
    fr: SearchMenuSvgFr,
    nl: SearchMenuSvgNl,
}

const Firefox = ({languageCode}) => {
    const detectedLang = languageCode || getLanguageCode()
    // fall back to English for languages we don't have copy for
    const lang = firefoxData[detectedLang] ? detectedLang : 'en'

    const {firefoxSeo, firefoxTitle, firefoxImageAlt, firefoxParagraphs, firefoxAvailability} = firefoxData[lang]

    const SearchMenuSvg = searchMenuSvgsByLang[lang] || SearchMenuSvgEn

    const [theme, setTheme] = useState('light')
    const [themeStyles, setThemeStyles] = useState({})

    useEffect(() => {
        setTheme(getTheme())
        setThemeStyles(getThemeStyles())
    }, [theme, themeStyles])

    return (
        <Layout
            title={firefoxSeo.title}
            description={firefoxSeo.description}
            lang={lang}
            canonicalPath="/firefox/"
            theme={theme}
            themeStyles={themeStyles}
        >
            <div
                css={css`
                    color: ${themeStyles.primaryTextColor};
                    background-color: ${themeStyles.primaryBackgroundColor};
                    margin: 5.4rem 0 0;
                    padding-top: 50px;

                    @media (min-width: 900px) {
                        padding-top: 60px;
                    }
                `}
                className="firefox-page"
            >
                <section
                    className="firefox-page__content"
                    css={css`
                        max-width: 800px;
                        margin: 0 auto;
                        padding: 0 16px 64px;

                        @media (min-width: 900px) {
                            padding-left: 40px;
                            padding-right: 40px;
                        }
                    `}
                >
                    <h1
                        css={css`
                            color: ${themeStyles.primaryTextColor};
                            font-weight: 700;
                            font-size: 38px;
                            line-height: 46px;
                            letter-spacing: 0.33px;
                            margin-bottom: 24px;

                            @media (min-width: 900px) {
                                font-size: 46px;
                                line-height: 48px;
                            }
                        `}
                    >
                        {firefoxTitle}
                    </h1>
                    {firefoxParagraphs.map((paragraph, i) => (
                        <p
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            css={css`
                                color: ${themeStyles.primaryTextColor};
                                font-size: 14px;
                                line-height: 24px;
                                font-weight: normal;
                                margin-top: 24px;

                                strong {
                                    color: inherit;
                                }

                                @media (min-width: 900px) {
                                    font-size: 16px;
                                    line-height: 28px;
                                }
                            `}
                            dangerouslySetInnerHTML={createMarkup(paragraph)}
                        />
                    ))}
                    <SearchMenuSvg
                        role="img"
                        aria-label={firefoxImageAlt}
                        css={css`
                            display: block;
                            width: 100%;
                            height: auto;
                            margin-top: 32px;
                            border-radius: 8px;
                        `}
                    />
                    <p
                        css={css`
                            color: ${themeStyles.primaryTextColor};
                            font-size: 14px;
                            line-height: 24px;
                            font-weight: normal;
                            margin-top: 24px;

                            @media (min-width: 900px) {
                                font-size: 16px;
                                line-height: 28px;
                            }
                        `}
                    >
                        {firefoxAvailability}
                    </p>
                </section>
            </div>
        </Layout>
    )
}

Firefox.propTypes = {
    languageCode: PropTypes.string,
}

export default Firefox
